import React, {useContext, useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useNavigate, useRouteLoaderData} from "react-router";
import {Formik} from "formik";
import * as yup from "yup";
import {Alert, Badge, Breadcrumb, Button, Col, Container, Form, Row, Spinner} from "react-bootstrap";
import {BackendContext} from "../../context/Backend";
import styles from "./usermanagement.module.css";

const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const parseEmails = (text) =>
    (text || "")
        .split(/[\r\n,;]+/)
        .map((e) => e.trim())
        .filter((e) => e.length > 0);

const Credits = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const backend = useContext(BackendContext);
    const {currentUser} = useRouteLoaderData("root");

    const [initialValues, setInitialValues] = useState(null);
    const [buyCreditsUrl, setBuyCreditsUrl] = useState(null);
    const [status, setStatus] = useState(null);

    useEffect(() => {
        backend.api.getNotificationSettings(null,
            (resp) => {
                setInitialValues({
                    informCreditAmount: resp.informCreditAmount ?? 0,
                    informMailList: (resp.informMailList || "")
                        .split(/[\s,;]+/)
                        .map((e) => e.trim())
                        .filter((e) => e.length > 0)
                        .join("\n"),
                });
                setBuyCreditsUrl(resp.buyCreditsUrl || null);
            },
            (error) => {
                navigate("/error", backend.api.getErrorState(error));
            });
    }, []);

    const schema = yup.object().shape({
        informCreditAmount: yup
            .number()
            .typeError(t('credits.amountInvalid'))
            .integer(t('credits.amountInvalid'))
            .min(0, t('credits.amountInvalid'))
            .required(t('credits.amountRequired')),
        informMailList: yup
            .string()
            .test('emails', t('credits.emailsInvalid'), (value) => {
                const emails = parseEmails(value);
                return emails.every((e) => EMAIL_REGEX.test(e));
            }),
    });

    if (!initialValues) {
        return (
            <Container fluid className="d-flex justify-content-center mt-5">
                <Spinner animation="border"/>
            </Container>
        );
    }

    return (
        <Container fluid>
            <Row className="justify-content-end">
                <Col className="d-flex justify-content-start">
                    <Breadcrumb className="mt-3">
                        <Breadcrumb.Item onClick={() => navigate("/")}>{t('breadcrumbs.home')}</Breadcrumb.Item>
                        <Breadcrumb.Item active>{t('credits.header')}</Breadcrumb.Item>
                    </Breadcrumb>
                </Col>
                <Col className="d-flex justify-content-end">
                    <div className={styles.credits}>
                        <h2 className="text-uppercase">
                            {t('credits.header')} <Badge bg="secondary">{currentUser.generalInfo.credits}</Badge>
                        </h2>
                    </div>
                </Col>
            </Row>
            <Formik
                validationSchema={schema}
                initialValues={initialValues}
                enableReinitialize
                onSubmit={(values, actions) => {
                    setStatus(null);
                    const payload = {
                        informCreditAmount: Number(values.informCreditAmount),
                        informMailList: parseEmails(values.informMailList).join(";"),
                    };
                    backend.api.updateNotificationSettings(payload,
                        () => {
                            setStatus({variant: "success", message: t('credits.saved')});
                            actions.setSubmitting(false);
                            actions.resetForm({values});
                        },
                        () => {
                            setStatus({variant: "danger", message: t('credits.saveFailed')});
                            actions.setSubmitting(false);
                        });
                }}>
                {({handleSubmit, handleChange, values, errors, isSubmitting, dirty, isValid}) => (
                    <Form onSubmit={handleSubmit} autoComplete="off">
                        <Row className="mt-3">
                            <Col>
                                <h3>{t('credits.header')}</h3>
                                <p className="text-muted">{t('credits.description')}</p>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label htmlFor="informCreditAmount">
                                        {t('credits.threshold')}
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        min={0}
                                        step={1}
                                        name="informCreditAmount"
                                        className={styles.creditInput}
                                        value={values.informCreditAmount}
                                        onChange={handleChange}
                                        isInvalid={!!errors.informCreditAmount}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.informCreditAmount}
                                    </Form.Control.Feedback>
                                    <Form.Text muted>{t('credits.thresholdHelp')}</Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label htmlFor="informMailList">
                                        {t('credits.recipients')}
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5}
                                        name="informMailList"
                                        style={{resize: "none"}}
                                        value={values.informMailList}
                                        onChange={handleChange}
                                        isInvalid={!!errors.informMailList}
                                        placeholder={t('credits.recipientsPlaceholder')}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.informMailList}
                                    </Form.Control.Feedback>
                                    <Form.Text muted>{t('credits.recipientsHelp')}</Form.Text>
                                </Form.Group>
                                {status && (
                                    <Alert variant={status.variant} className="mt-3">
                                        {status.message}
                                    </Alert>
                                )}
                                <div className="d-flex gap-2 justify-content-end">
                                    {buyCreditsUrl && (
                                        <Button
                                            as="a"
                                            className="websobutton"
                                            href={buyCreditsUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {t('credits.buyButton')}
                                        </Button>
                                    )}
                                    <Button
                                        className="websobutton"
                                        type="submit"
                                        disabled={isSubmitting || !dirty || !isValid}
                                    >
                                        {isSubmitting ? (
                                            <Spinner className="me-2" as="span" animation="border" size="sm"/>
                                        ) : null}
                                        {t('system.saveButton')}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                )}
            </Formik>
        </Container>
    );
};

export default Credits;
